import { LitElement, nothing, type PropertyValues, unsafeCSS } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import type { SpriteSheetState } from '../../../app/package/base/service/render/render-service';
import type { CallbackFn } from '../../base/package/types';
import style from './ui-sprite-svg.css?inline';

const CName = 'ui-sprite-svg';

/**
 * @prop {string} iconID - Icon ID for the SVG to load from sprite sheet.
 * @prop {string} class - Class that will be applied to SVG.
 * @prop {string} label - Accessible label for the SVG. When provided, sets role="img" and aria-label instead of aria-hidden="true".
 */
@customElement(CName)
export class UISpriteSvg extends LitElement {
  static readonly styles = unsafeCSS(style);

  @property({ type: String }) iconID: string;
  @property({ type: String }) class = '';
  @property({ type: String }) label = '';

  @state() private _svgContent: string;

  private _sub: CallbackFn<SpriteSheetState> | null = null;
  private _spriteSheetReady = false;

  protected willUpdate(_changedProperties: PropertyValues) {
    // Update SVG content when sprite sheet is ready and iconID or label changes
    if ((_changedProperties.has('iconID') || _changedProperties.has('label')) && this._spriteSheetReady) {
      this._setSvgContent();
    }
  }

  connectedCallback() {
    super.connectedCallback();
    const store = window.$app.renderService.spriteSheetStore;

    // Synchronous check — avoids a persistent subscription when the
    // sprite sheet is already loaded (the common case at scale).
    if (store.value.ready) {
      this._spriteSheetReady = true;
      this._setSvgContent();
      return;
    }

    // Not yet ready — use subscribeOnce so the subscription self-destructs
    // after the first emission. No persistent listener retained.
    this._sub = store.subscribeOnce((spriteState) => {
      this._sub = null;
      if (spriteState.ready) {
        this._spriteSheetReady = true;
        this._setSvgContent();
      }
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this._sub) {
      window.$app.renderService.spriteSheetStore.unsubscribe(this._sub);
      this._sub = null;
    }
  }

  render() {
    return this._svgContent && this.iconID !== undefined ? unsafeHTML(this._svgContent) : nothing;
  }

  private _setSvgContent() {
    this._svgContent = this._transformSymbolToSvgText(this.iconID, this.class, this.label);
  }

  private _transformSymbolToSvgText(symbolId: string, className: string, label: string) {
    const symbol = document.getElementById(symbolId);
    if (!symbol) {
      window.$app.logger.error(`Symbol with ID ${symbolId} not found.`, symbolId);
      return '';
    }

    let svgText = symbol.outerHTML;

    if (label) {
      const escapedLabel = label.replaceAll('&', '&amp;').replaceAll('"', '&quot;');
      svgText = svgText.replace('<symbol', `<svg class="${className}" role="img" aria-label="${escapedLabel}"`);
    } else {
      svgText = svgText.replace('<symbol', `<svg class="${className}" aria-hidden="true"`);
    }
    svgText = svgText.replace('</symbol>', '</svg>');

    return svgText;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    [CName]: UISpriteSvg;
  }
}
